import { Store } from '../store'; // eslint-disable-line no-unused-vars
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  getUserPool,
  exchangeCodeForTokens,
  getCognitoUser,
  forceRefreshSession
} from '../../api/cognito';
import {
  COMPANY_PROFILE_FIELDS,
  USER_PROFILE_FIELDS,
  USER_CHANGE_PASSWORD_FIELDS,
  isEmptyValue
} from '../../utils/constants/fields';
import { generateMessage } from '../../utils/utils';
import { API_ENDPOINTS } from '../../api/endpoints';
import { recordSessionRelease } from '../../utils/sessionClaim';
import {
  ACCOUNT_VERIF_TYPES,
  ACCOUNT_TYPES,
  MFA_LOCKED_CODE,
  MFA_LOCK_FALLBACK_SECONDS
} from '../../utils/constants/auth';
import { mapData } from '../../api/dataMappers';
import {
  eventSignUpStep1,
  eventSignUpStep2,
  eventSignUpSubmitted,
  eventSignUpCompleted,
  eventSignUpFailed,
  eventLoginFailed,
  eventLogout,
  eventPasswordResetRequested
} from '../../ga4/ga4';
import { HEADER_MESSAGE_TYPES } from '../../utils/constants/header';
import pubSub from '../../utils/pubsub';

// Cognito error name -> the GA4 login_failed reason. Anything unmapped reports
// its own error name, or 'unknown' when Cognito gave us neither.
const LOGIN_FAILURE_REASONS = {
  NotAuthorizedException: 'invalid_password',
  PasswordResetRequiredException: 'password_reset_required'
};

export const SESSION_REFRESH_TIMEOUT_MS = 15000;

// Refreshes the signed-in user's session after an MFA change. refreshSession
// reads the existing session, so getSession runs first. Capped so a hung
// refresh can't hold the MFA in-flight flags (isAuthenticating is shared with
// every auth form) forever; the timeout rejects like any refresh failure.
const refreshCurrentSession = (cognitoUser) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    const error = new Error('Session refresh timed out. Please sign in again.');
    error.name = 'TimeoutError';
    timer = setTimeout(() => reject(error), SESSION_REFRESH_TIMEOUT_MS);
  });
  const refresh = new Promise((resolve, reject) => {
    cognitoUser.getSession((err) => (err ? reject(err) : resolve()));
  }).then(() => forceRefreshSession(cognitoUser));
  return Promise.race([refresh, timeout]).finally(() => clearTimeout(timer));
};

class AuthStore {
  /**
   * @param {Store} root
   */
  constructor(root) {
    makeAutoObservable(this, { mfaCodeLockTimer: false });
    this.root = root;
  }

  /**
   * Restores the stored session and finishes a LinkedIn sign-in, then loads the
   * account details once both are done. store.js calls this once, after every
   * store exists. A failed session restore or LinkedIn exchange no longer
   * leaves the app on its loader; a restore that never answers still does
   * (getSession has no timeout here, as before).
   */
  init = async () => {
    // Only the restore can reject: the LinkedIn sign-in handles its own errors.
    const [restore] = await Promise.allSettled([
      this.restoreSession(),
      this.completeLinkedInSignIn()
    ]);
    if (restore.status === 'rejected') {
      console.error('Session restore failed', restore.reason);
    }
    this.getAccountDetails();
  };

  // Resolves once getSession has answered. The callback sets `user` itself: a
  // valid cached session answers synchronously, so it is in place before the
  // first render.
  restoreSession = () =>
    new Promise((resolve) => {
      const cognitoUser = getUserPool().getCurrentUser();
      if (!cognitoUser) {
        resolve();
        return;
      }
      cognitoUser.getSession((error, session) => {
        if (!error) {
          runInAction(() => {
            this.user = session;
          });
        }
        resolve();
      });
    });

  // LinkedIn authorization code flow: Cognito redirects back to
  // /?authenticate&code=..., and the code is exchanged for tokens here.
  completeLinkedInSignIn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (!code || !urlParams.has('authenticate')) {
      return;
    }
    try {
      const session = await exchangeCodeForTokens(code);
      runInAction(() => {
        this.user = session;
      });
    } catch (error) {
      console.error('LinkedIn sign-in failed', error);
    } finally {
      // A code is single-use: left in the URL, a reload would fail again. The
      // router keeps its own data in history.state, so pass that on unchanged.
      window.history.replaceState(window.history.state, document.title, window.location.pathname);
    }
  };

  reset = () => {
    this.showLoginVerifyScreen = null;
    this.user = null;
    this.alreadyVerifiedEmail = null;
    this.userInfo = null;
    this.accountInfoError = null;
    this.isUpdatingUserInfo = false;
    this.userAccountType = ACCOUNT_TYPES.NOT_OBTAINED;
    this.userVerificationStatus = ACCOUNT_VERIF_TYPES.NOT_OBTAINED;
    this.userCompanyType = null;
    this.hasUserProfileCompany = false;
    this.userProfileCompany = null;
    this.isAuthenticating = false;
    this.authErrors = { messages: [], invalidFields: [] };
    this.signUpStep = 1;
    this.signUpCognitoUser = null;
    this.forgottenPwdStep = 1;
    this.companyUsers = [];
    this.isLoadingCompanyUsers = false;
    this.companyUsersRequest = null;
    this.companyUsersEpoch += 1;
    // TD-1976: MFA pending state must not survive a logout / store reset.
    this.loginMfaPending = false;
    this.pendingMfaCognitoUser = null;
    this.loginMfaEmail = '';
    // TD-1976 Phase 4: setup-secret payload + sub-state must not survive logout.
    this.mfaSetupStep = 'setup';
    this.mfaSetupSecret = null;
    this.mfaSetupError = null;
    this.mfaSetupMode = 'enroll';
    // TD-1976 Phase 6: settings → security tab state.
    this.mfaStatus = null;
    this.mfaStatusError = null;
    this.isLoadingMfaStatus = false;
    this.mfaDisableError = null;
    this.isDisablingMfa = false;
    // TD-2192: a lockout belongs to the signed-in user.
    clearTimeout(this.mfaCodeLockTimer);
    this.mfaCodeLockTimer = null;
    this.mfaCodeLockMessage = null;
    this.mfaCodeLockedUntil = null;
  };

  showLoginVerifyScreen = null;
  user = null;
  alreadyVerifiedEmail = null;
  userInfo = null;
  accountInfoError = null;
  isUpdatingUserInfo = false;
  userAccountType = ACCOUNT_TYPES.OBTAINING;
  userVerificationStatus = ACCOUNT_VERIF_TYPES.OBTAINING;
  userCompanyType = null;
  hasUserProfileCompany = false;
  userProfileCompany = null;
  isAuthenticating = true; // auth action flag for every auth page
  authErrors = { messages: [], invalidFields: [] }; // auth error for every auth page

  signUpStep = 1; // 1 - register, 2 - verify code, 3 - all done
  signUpCognitoUser = null;
  signupHasPhone = false;

  forgottenPwdStep = 1;

  companyUsers = [];
  isLoadingCompanyUsers = false;
  // Shared in-flight request, so concurrent callers get one fetch and each can
  // await the result. `makeAutoObservable` makes this observable like every other
  // field; nothing renders from it, so read `isLoadingCompanyUsers` for UI state
  // rather than tracking a promise in a reaction.
  companyUsersRequest = null;
  // Bumped on reset so a response still in flight across a logout cannot write
  // the next user's store with the previous company's colleagues.
  companyUsersEpoch = 0;

  // TD-1976 Phase 5 — Login TOTP challenge state.
  // Cognito returns ChallengeName: SOFTWARE_TOKEN_MFA after a successful password
  // check; the SDK surfaces it through the `totpRequired` callback. We pause the
  // login flow there, surface a 6-digit code step to the user, and call
  // `sendMFACode(code, callbacks, 'SOFTWARE_TOKEN_MFA')` on the SAME CognitoUser
  // instance to complete the login. The instance MUST be preserved between the
  // initial authenticateUser call and the eventual sendMFACode call.
  loginMfaPending = false;
  pendingMfaCognitoUser = null;
  loginMfaEmail = '';

  // TD-1976 Phase 4 — MFA setup flow state.
  // Three sub-states drive the /mfa-setup page: 'setup' (scan QR), 'verify'
  // (enter 6-digit code), 'success' (refresh session and unlock).
  // `mfaSetupSecret` holds the one-shot `{ otpauthUri, secretCode }` returned
  // by POST /mfa/setup. It is **only** read by the page and the QR/manual-entry
  // DOM; never logged, never sent to GA4, never persisted. Cleared on unmount
  // and on successful verify.
  mfaSetupStep = 'setup';
  mfaSetupSecret = null;
  mfaSetupError = null;
  // 'enroll' — first-time setup (initial /mfa/setup with empty body).
  // 'rotate' — user already has MFA enabled and is generating a new setup
  //   key. /mfa/setup is called with { userCode } so the backend can verify
  //   possession of the current authenticator before issuing a new secret
  //   (rotation guard at td-backend/lambdas-auxbe/controllers/mfa.js:87).
  //   The new token replaces the current one only once verified, so rotation
  //   is complete only after verify — see TabSecurity.RotateView.
  mfaSetupMode = 'enroll';

  // TD-1976 Phase 6 — Settings → Security → MFA state.
  // `mfaStatus` mirrors GET /mfa/status: { enabled, required }. Backend derives
  // `required = !enabled && inGroup`, so `enabled && required` should never be
  // observed. The Security tab renders three reachable variants:
  //   { enabled: false, required: false } → "Not enabled", Enable CTA
  //   { enabled: true,  required: false } → "Enabled", Disable CTA
  //   { enabled: false, required: true  } → "Setup pending" alert, Continue → /mfa-setup
  mfaStatus = null;
  mfaStatusError = null;
  isLoadingMfaStatus = false;
  mfaDisableError = null;
  isDisablingMfa = false;

  // TD-2192 — backend MFA code lockout. Verify, disable and rotation share one
  // backend counter, so one lock disables code entry in all three. Holds the
  // backend's text so the views can keep showing it after their own error is
  // cleared (Back, Cancel, typing). `mfaCodeLockedUntil` (epoch ms) drives
  // the views' countdown. The timer is not observable.
  mfaCodeLockMessage = null;
  mfaCodeLockedUntil = null;
  mfaCodeLockTimer = null;

  get isMfaCodeLocked() {
    return !!this.mfaCodeLockMessage;
  }

  get currentUserId() {
    return this.userInfo?.id;
  }

  get isCoraporatesInvestor() {
    return this.userProfileCompany?.[COMPANY_PROFILE_FIELDS.COMPANY_TYPE.NAME]?.[0] === 3;
  }

  get isLoadingAccountDetails() {
    return (
      this.userVerificationStatus === ACCOUNT_VERIF_TYPES.OBTAINING ||
      this.userAccountType === ACCOUNT_TYPES.OBTAINING
    );
  }

  get isUserProfileCompanyCardPending() {
    return !!Object.values(COMPANY_PROFILE_FIELDS)
      .map((r) => r.NAME)
      .find((fieldName) => isEmptyValue(this.userProfileCompany?.[fieldName]));
  }

  get isUserProfileCardPending() {
    return !!Object.values(USER_PROFILE_FIELDS).find((f) => isEmptyValue(this.userInfo?.[f.NAME]));
  }

  cleanUpAuthPage = () => {
    runInAction(() => {
      this.authErrors = { messages: [], invalidFields: [] };

      this.signUpStep = 1;
      this.signUpCognitoUser = null;

      this.forgottenPwdStep = 1;

      // TD-1976: a pending login MFA challenge should not survive navigation away
      // from the auth pages — the cognitoUser ref becomes meaningless once the
      // user leaves the flow.
      this.loginMfaPending = false;
      this.pendingMfaCognitoUser = null;
      this.loginMfaEmail = '';
    });
  };

  // Resolves with the company users, or NULL when the request failed, so a
  // caller that needs the list for its own work can await this instead of
  // fetching the endpoint a second time — and can tell a failure from an empty
  // company. The in-flight promise is shared: the plain `isLoadingCompanyUsers`
  // guard alone let a concurrent caller return with nothing to await.
  loadCompanyUsers = () => {
    if (this.companyUsersRequest) {
      return this.companyUsersRequest;
    }

    this.isLoadingCompanyUsers = true;

    // The promise is published BEFORE the request goes out. Assigning it after
    // makeRequest returns would let a synchronous onFinally null the field
    // inside the executor, only for the outer assignment to put the settled
    // promise back — wedging the guard so no later call ever fetched again.
    let resolveRequest;
    const pending = new Promise((resolve) => {
      resolveRequest = resolve;
    });
    this.companyUsersRequest = pending;

    const epoch = this.companyUsersEpoch;
    let failed = false;

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.GET_SELF_COMPANY_USERS,
      onSuccess: (response) => {
        if (epoch === this.companyUsersEpoch) {
          this.companyUsers = response;
        }
      },
      onError: () => {
        failed = true;
      },
      onFinally: () => {
        this.isLoadingCompanyUsers = false;
        if (this.companyUsersRequest === pending) {
          this.companyUsersRequest = null;
        }
        resolveRequest(failed || epoch !== this.companyUsersEpoch ? null : this.companyUsers);
      }
    });

    return pending;
  };

  refreshProfilerDemoCounter = () => {
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.GET_USER_INFO,
      onSuccess: (response) => {
        // Guard against a logout / store reset landing between request and
        // response — this reconcile now fires after every profiler search
        // (agent + classic), so the null window is easy to hit.
        if (!this.userInfo) {
          return;
        }
        this.userInfo.profilerSearchExecutedCount = response.profilerSearchExecutedCount;
        this.userInfo.profilerSearchMaxCount = response.profilerSearchMaxCount;
      }
    });
  };

  signup = (fields = {}) => {
    const { email, password } = fields;
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    let messages = [];
    let invalidFields = [];

    if (invalidFields.length) {
      runInAction(() => {
        this.isAuthenticating = false;
        this.authErrors = { messages, invalidFields };
      });
      return;
    }

    // validation is OK

    const userPool = getUserPool();
    const attributeList = [];
    const skipFields = ['password'];
    const customFields = ['company_name', 'company_type', 'phone_code' /*'marketing_consent'*/];
    for (const [key, value] of Object.entries(fields)) {
      let keyName = key;
      if (skipFields.includes(key)) {
        continue;
      }
      if (customFields.includes(key)) {
        keyName = `custom:${key}`;
      }
      if (keyName === 'phone_number') {
        if (!value) {
          continue;
        }

        attributeList.push(
          new AmazonCognitoIdentity.CognitoUserAttribute({
            Name: keyName,
            Value: fields['phone_code'] + value
          })
        );
      } else {
        if (value) {
          attributeList.push(
            new AmazonCognitoIdentity.CognitoUserAttribute({
              Name: keyName,
              Value: value
            })
          );
        }
      }
    }
    const opts = this.root.utilsStore?.options?.companyTypes;
    const companyTypeReadable = opts?.find((opt) => opt.value == fields['company_type'])?.name;
    this.userCompanyType = companyTypeReadable;
    this.signupHasPhone = !!fields.phone_number;

    // Signup form submitted (fires regardless of the eventual server result).
    eventSignUpSubmitted(this.userCompanyType, this.signupHasPhone);

    // TODO: any captcha to be sent as validationData
    // const validationData = [{ Name: 'foo', Value: 'bar' }];
    const validationData = null;

    userPool.signUp(email, password, attributeList, validationData, (error, result) => {
      if (error) {
        if (error.name === 'UsernameExistsException') {
          eventSignUpFailed('email_exists', 'email');
          runInAction(() => {
            messages.push(
              generateMessage(
                'Registration not possible, please contact info@transact.digital',
                false
              )
            );
            this.isAuthenticating = false;
            this.authErrors = { messages, invalidFields };
          });

          return;
        }

        eventSignUpFailed('server_error');
        messages.push(generateMessage(error.message, false));
        runInAction(() => {
          this.isAuthenticating = false;
          this.authErrors = { messages, invalidFields };
        });
      } else {
        eventSignUpStep1(this.userCompanyType);

        runInAction(() => {
          this.isAuthenticating = false;
          this.signUpCognitoUser = result.user;
          this.signUpStep = 2;
        });
      }
    });
  };

  resendSignUpCode = (UsernameExistsException = false) => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    this.signUpCognitoUser.resendConfirmationCode((error) => {
      if (error) {
        if (error.name === 'InvalidParameterException' && UsernameExistsException) {
          runInAction(() => {
            this.isAuthenticating = false;
            this.alreadyVerifiedEmail = this.signUpCognitoUser.username;
          });
        } else {
          runInAction(() => {
            this.isAuthenticating = false;
            this.authErrors = {
              messages: [generateMessage(error.message, false)],
              invalidFields: []
            };
          });
          return;
        }
      }

      runInAction(() => {
        this.isAuthenticating = false;
      });
    });
  };

  confirmSignUp = (verificationCode, email, password) => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
      this.signUpCognitoUser.confirmRegistration(verificationCode, true, (error) => {
        if (error) {
          eventSignUpFailed('validation', 'code');
          runInAction(() => {
            this.isAuthenticating = false;
            this.authErrors = {
              messages: [generateMessage(error.message, false)],
              invalidFields: []
            };
          });
          return;
        }

        eventSignUpStep2(this.userCompanyType);
        eventSignUpCompleted(this.userCompanyType, this.signupHasPhone);

        runInAction(() => {
          this.isAuthenticating = false;
          this.signUpStep = 3;

          if (
            (email || this.showLoginVerifyScreen?.email) &&
            (password || this.showLoginVerifyScreen?.password)
          ) {
            this.isAuthenticating = true;
            setTimeout(
              () => {
                this.login(
                  email || this.showLoginVerifyScreen?.email,
                  password || this.showLoginVerifyScreen?.password
                );
              },
              +(import.meta.env.VITE_SIGNUP_LOGIN_TIMEOUT_MS || 3_000)
            );
          }
        });
      });
    });
  };

  /**
   * Build the shared Cognito callbacks for the password-then-TOTP login flow.
   *
   * Both `authenticateUser` (Phase 1: password) and `sendMFACode` (Phase 2:
   * TOTP) report through the SAME `onSuccess` / `onFailure` shape. Extracting
   * the callbacks here keeps the two entry points consistent — in particular,
   * the TOTP-success path goes through this `onSuccess` once `sendMFACode`
   * resolves, which already calls `getAccountDetails()`.
   *
   * The closure captures `email` / `password` so the UserNotConfirmedException
   * branch can resume the verify-code flow exactly as the original login did.
   *
   * @private
   */
  _buildLoginCallbacks = (email, password) => ({
    onSuccess: (result) => {
      runInAction(() => {
        this.user = result;
        this.isAuthenticating = false;
        // Clear any MFA-pending residue once the session is established.
        this.loginMfaPending = false;
        this.pendingMfaCognitoUser = null;
        this.loginMfaEmail = '';
      });
      this.getAccountDetails();
    },
    onFailure: (error) => {
      if (error.name === 'UserNotFoundException') {
        eventLoginFailed('user_not_found');
      } else if (error.name !== 'UserNotConfirmedException') {
        eventLoginFailed(LOGIN_FAILURE_REASONS[error.name] || error.name || 'unknown');
      }
      runInAction(() => {
        if (error.name === 'UserNotConfirmedException') {
          this.signUpCognitoUser = getCognitoUser(email);
          this.signUpStep = 2;
          this.showLoginVerifyScreen = { email, password };
          this.resendSignUpCode(true);
        } else if (error.name === 'UserNotFoundException') {
          this.isAuthenticating = false;
          this.authErrors = {
            messages: [generateMessage('Incorrect username or password.', false)],
            invalidFields: []
          };
        } else {
          this.isAuthenticating = false;
          this.authErrors = {
            messages: [generateMessage(error.message, false)],
            invalidFields: []
          };
        }
      });
    }
  });

  login = (email = '', password = '') => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    const authenticationData = {
      Username: email,
      Password: password
    };

    const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      authenticationData
    );

    // Capture the CognitoUser instance up-front so the `totpRequired` callback
    // can stash it for the eventual sendMFACode call. Cognito's SDK requires
    // sendMFACode to be called on the SAME instance that initiated
    // authenticateUser — building a fresh one with getCognitoUser(email) at
    // that point loses the in-flight challenge context and fails.
    const cognitoUser = getCognitoUser(email);
    const callbacks = this._buildLoginCallbacks(email, password);

    cognitoUser.authenticateUser(authenticationDetails, {
      ...callbacks,
      // Cognito-managed TOTP MFA — surfaced when ChallengeName === 'SOFTWARE_TOKEN_MFA'.
      // We do NOT consider this a failure: the password was correct, we just need
      // a second factor. Park the user on the OTP step until they submit a code.
      totpRequired: () => {
        runInAction(() => {
          this.isAuthenticating = false;
          this.pendingMfaCognitoUser = cognitoUser;
          this.loginMfaEmail = email;
          this.loginMfaPending = true;
        });
      }
    });
  };

  /**
   * Submit the 6-digit TOTP code that completes a login challenge.
   *
   * Must be called only when `loginMfaPending === true`. Uses the CognitoUser
   * instance captured during the original `login()` call — Cognito's SDK
   * routes the response back into the same authentication flow it paused on.
   *
   * The third argument 'SOFTWARE_TOKEN_MFA' is MANDATORY — without it the SDK
   * defaults to SMS MFA and fails with NotAuthorizedException.
   *
   * On success the shared `onSuccess` callback runs `getAccountDetails`. On
   * failure we surface the error inline and keep `loginMfaPending=true` so
   * the user can retry without re-entering their password. We do NOT log
   * out on a bad code — the SDK is still holding the challenge open.
   */
  submitLoginMfaCode = (code = '') => {
    if (!this.pendingMfaCognitoUser) {
      return;
    }

    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    const baseCallbacks = this._buildLoginCallbacks(this.loginMfaEmail, '');

    // Wrap onFailure so a bad code does NOT clear the pending-MFA state; the
    // user should be able to retype the code without re-entering their password.
    const mfaCallbacks = {
      onSuccess: baseCallbacks.onSuccess,
      onFailure: (error) => {
        runInAction(() => {
          this.isAuthenticating = false;
          this.authErrors = {
            messages: [generateMessage(error?.message || 'Invalid code', false)],
            invalidFields: []
          };
          // Keep loginMfaPending / pendingMfaCognitoUser intact for retry.
        });
      }
    };

    this.pendingMfaCognitoUser.sendMFACode(code, mfaCallbacks, 'SOFTWARE_TOKEN_MFA');
  };

  /**
   * Abandon the in-flight TOTP challenge — used by the "Back to sign in" link
   * on the MFA step. Drops the cognitoUser reference so the next login starts
   * a fresh authenticateUser call.
   */
  cancelLoginMfa = () => {
    runInAction(() => {
      this.loginMfaPending = false;
      this.pendingMfaCognitoUser = null;
      this.loginMfaEmail = '';
      this.authErrors = { messages: [], invalidFields: [] };
      this.isAuthenticating = false;
    });
  };

  requestResetPassword = (email) => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    const cognitoUser = getCognitoUser(email);
    cognitoUser.forgotPassword({
      onSuccess: () => {
        eventPasswordResetRequested();
        runInAction(() => {
          this.isAuthenticating = false;
          this.forgottenPwdStep = 2;
        });
      },
      onFailure: (error) => {
        runInAction(() => {
          this.isAuthenticating = false;
          this.authErrors = {
            messages: [generateMessage(error.message, false)],
            invalidFields: []
          };
        });
      }
    });
  };

  submitPasswordReset = (email, code, password) => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = { messages: [], invalidFields: [] };
    });

    // validate new password match - not having repeatPassword now
    // if (password !== passwordRepeat) {
    //   runInAction(() => {
    //     this.isAuthenticating = false;
    //     this.authError = 'Passwords do not match';
    //   });
    //   return;
    // }

    const cognitoUser = getCognitoUser(email);
    cognitoUser.confirmPassword(code, password, {
      onSuccess: () => {
        runInAction(() => {
          this.isAuthenticating = false;
          this.forgottenPwdStep = 3;
        });
      },
      onFailure: (error) => {
        runInAction(() => {
          this.isAuthenticating = false;
          this.authErrors = {
            messages: [generateMessage(error.message, false)],
            invalidFields: []
          };
        });
      }
    });
  };

  /**
   * TD-2192 — lock MFA code entry when the backend says the user is locked
   * out (`code: 'MFA_LOCKED'`), until its `retryAfterSeconds` runs out. A
   * later locked response restarts the timer from its own value. Other errors
   * leave the lock alone. Call inside an action.
   */
  lockMfaCodesIfLocked = (response, message) => {
    if (response?.code !== MFA_LOCKED_CODE) {
      return;
    }
    const seconds = response.retryAfterSeconds;
    const lockSeconds =
      Number.isFinite(seconds) && seconds > 0 ? seconds : MFA_LOCK_FALLBACK_SECONDS;

    clearTimeout(this.mfaCodeLockTimer);
    this.mfaCodeLockMessage = message;
    this.mfaCodeLockedUntil = Date.now() + lockSeconds * 1000;
    this.mfaCodeLockTimer = setTimeout(this.releaseMfaCodeLock, lockSeconds * 1000);
  };

  /**
   * Ends the MFA code lock. The flow error that still holds the lock text is
   * cleared too, or the views would keep showing it after the lock.
   */
  releaseMfaCodeLock = () => {
    const message = this.mfaCodeLockMessage;
    if (this.mfaSetupError === message) {
      this.mfaSetupError = null;
    }
    if (this.mfaDisableError === message) {
      this.mfaDisableError = null;
    }
    clearTimeout(this.mfaCodeLockTimer);
    this.mfaCodeLockMessage = null;
    this.mfaCodeLockedUntil = null;
    this.mfaCodeLockTimer = null;
  };

  /**
   * Ends the lock once its time is up. The countdown calls this every second:
   * the lock timer can fire late (laptop sleep, background tab), which would
   * leave code entry blocked with the countdown at 0:00.
   */
  releaseMfaCodeLockIfExpired = () => {
    if (this.mfaCodeLockedUntil && Date.now() >= this.mfaCodeLockedUntil) {
      this.releaseMfaCodeLock();
    }
  };

  /**
   * TD-1976 Phase 4 — fetch the TOTP secret from `POST /mfa/setup`.
   *
   * Stores `{ otpauthUri, secretCode }` in `mfaSetupSecret` for the page to
   * render. The endpoint requires BOTH the ID token (Authorization) and the
   * Cognito access token (`x-cognito-access-token`); store.makeRequest +
   * the api.js header injector handle that for us.
   *
   * Two modes:
   *   - 'enroll' (default) — initial setup. Empty body. Backend's rotation
   *     guard at mfa.js:87 only triggers when the user already has
   *     SOFTWARE_TOKEN_MFA enabled, so the empty body is correct here.
   *   - 'rotate' — user already has MFA enabled and is generating a new
   *     setup key. Caller must supply the current `userCode`; the backend
   *     verifies it via VerifySoftwareToken before calling
   *     AssociateSoftwareToken. Failure ("Invalid verification code") lands
   *     in `mfaSetupError`.
   *
   * NOTE on rotation: Cognito replaces the current software token only when
   * the new one is verified, so the previous authenticator keeps working
   * until verifyMfaSetup succeeds. Aborting mid-rotation is safe.
   *
   * Mirrors the `requestResetPassword` pattern — sets `isAuthenticating`,
   * clears `mfaSetupError`, surfaces failures into `mfaSetupError`.
   */
  startMfaSetup = (userCode = '', mode = 'enroll') => {
    runInAction(() => {
      this.isAuthenticating = true;
      this.mfaSetupError = null;
      this.mfaSetupStep = 'setup';
      this.mfaSetupSecret = null;
      this.mfaSetupMode = mode;
    });

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.MFA_SETUP,
      body: userCode ? { userCode } : {},
      onSuccess: (data) => {
        runInAction(() => {
          this.mfaSetupSecret = {
            otpauthUri: data?.otpauthUri || '',
            secretCode: data?.secretCode || ''
          };
        });
      },
      onError: (errorMessage, response) => {
        runInAction(() => {
          this.mfaSetupError = errorMessage || 'Failed to start MFA setup.';
          this.lockMfaCodesIfLocked(response, this.mfaSetupError);
        });
      },
      onFinally: () => {
        runInAction(() => {
          this.isAuthenticating = false;
        });
      }
    });
  };

  /**
   * TD-1976 Phase 4 — submit the 6-digit code to `POST /mfa/verify`, then
   * force a Cognito session refresh so the new ID token drops the
   * `custom:mfa_setup_required` claim. Once the refreshed session is written
   * to `this.user`, the route guard releases on the next pass.
   *
   * On success: advances to `success` sub-state, replaces `this.user` with the
   * refreshed session, clears `mfaSetupSecret` (hygiene — the secret is no
   * longer needed and we don't want it sitting in memory).
   *
   * On error: keeps the user on the `verify` sub-state with `mfaSetupError`
   * populated; do NOT bounce back to `setup` because the user may want to
   * retry with a fresh code.
   *
   * `isAuthenticating` stays set until the session refresh settles.
   * makeRequest does not await an async onSuccess, so clearing it in
   * onFinally would re-enable Verify mid-refresh and let a second click send
   * the code again.
   */
  verifyMfaSetup = (userCode = '') => {
    if (this.isAuthenticating) {
      return;
    }
    runInAction(() => {
      this.isAuthenticating = true;
      this.mfaSetupError = null;
    });

    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.MFA_VERIFY,
      body: { userCode },
      onSuccess: async () => {
        try {
          const cognitoUser = getUserPool().getCurrentUser();
          if (!cognitoUser) {
            throw new Error('Session expired. Please sign in again.');
          }
          const session = await refreshCurrentSession(cognitoUser);
          runInAction(() => {
            this.user = session;
            this.mfaSetupStep = 'success';
            // Drop the secret — it's served its purpose and shouldn't linger.
            this.mfaSetupSecret = null;
          });
        } catch (err) {
          runInAction(() => {
            this.mfaSetupError =
              err?.message ||
              'Verification succeeded but session refresh failed. Please sign in again.';
          });
        } finally {
          runInAction(() => {
            this.isAuthenticating = false;
          });
        }
      },
      onError: (errorMessage, response) => {
        runInAction(() => {
          this.mfaSetupError = errorMessage || 'Invalid code. Please try again.';
          this.lockMfaCodesIfLocked(response, this.mfaSetupError);
          this.isAuthenticating = false;
        });
      }
    });
  };

  /**
   * TD-1976 Phase 4 — local sub-state transitions inside the MFA setup page.
   * Kept here (rather than as local component state) so the page can be
   * remounted without losing the user's place mid-flow.
   */
  setMfaSetupStep = (step) => {
    runInAction(() => {
      this.mfaSetupStep = step;
      // Clearing the error on step change avoids the verify error sticking to
      // the setup screen if the user navigates back to scan again.
      this.mfaSetupError = null;
    });
  };

  /**
   * TD-1976 Phase 4 — call from the MFA setup page's unmount effect.
   * Drops the in-memory secret payload so it does not survive navigation.
   */
  clearMfaSetup = () => {
    runInAction(() => {
      this.mfaSetupStep = 'setup';
      this.mfaSetupSecret = null;
      this.mfaSetupError = null;
      this.mfaSetupMode = 'enroll';
    });
  };

  /**
   * TD-1976 Phase 6 — fetch `GET /mfa/status`.
   *
   * Result `{ enabled, required }` lands on `this.mfaStatus`. The Security tab
   * reads it synchronously after the first load. Re-runs of this action update
   * the status in place; failures populate `mfaStatusError` but do not clear
   * the last known status.
   */
  loadMfaStatus = () => {
    if (this.isLoadingMfaStatus) {
      return;
    }
    runInAction(() => {
      this.isLoadingMfaStatus = true;
      this.mfaStatusError = null;
    });
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.MFA_STATUS,
      onSuccess: (data) => {
        runInAction(() => {
          this.mfaStatus = {
            enabled: !!data?.enabled,
            required: !!data?.required
          };
        });
      },
      onError: (errorMessage) => {
        runInAction(() => {
          this.mfaStatusError = errorMessage || 'Failed to load MFA status.';
        });
      },
      onFinally: () => {
        runInAction(() => {
          this.isLoadingMfaStatus = false;
        });
      }
    });
  };

  /**
   * TD-1976 Phase 6 — `DELETE /mfa/disable { userCode }`.
   *
   * Disable is **not** silent — backend requires a current TOTP code as a
   * re-auth step. On success: refresh status, optionally `forceRefreshSession`
   * to pick up new claims, clear any cached setup secret.
   *
   * Failure surfaces in `mfaDisableError` so the Security tab can show it
   * next to the OtpInput. Two notable backend error shapes the UI keys off:
   *   - "Invalid verification code" → put OtpInput in error state, allow retry
   *   - group-blocked ("MFA is required for your account and cannot be disabled")
   *     → the user shouldn't have been able to click Disable; the alert from
   *     row 1 already explains. Treat as an error message either way.
   *
   * Like verifyMfaSetup, `isDisablingMfa` stays set until the session refresh
   * settles, so Disable cannot be sent twice.
   */
  disableMfa = (userCode = '') => {
    if (this.isDisablingMfa) {
      return;
    }
    runInAction(() => {
      this.isDisablingMfa = true;
      this.mfaDisableError = null;
    });
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.MFA_DISABLE,
      body: { userCode },
      onSuccess: async () => {
        try {
          const cognitoUser = getUserPool().getCurrentUser();
          if (cognitoUser) {
            const session = await refreshCurrentSession(cognitoUser);
            runInAction(() => {
              this.user = session;
            });
          }
        } catch (err) {
          // Refresh failure here is non-fatal — disable succeeded, but the
          // user may see slightly stale claims until the next API call. Don't
          // surface this as a user-facing error.
          console.warn('MFA disable: session refresh failed', err?.name);
        }
        runInAction(() => {
          this.mfaStatus = { enabled: false, required: false };
          this.isDisablingMfa = false;
        });
      },
      onError: (errorMessage, response) => {
        runInAction(() => {
          this.mfaDisableError = errorMessage || 'Failed to disable MFA.';
          this.lockMfaCodesIfLocked(response, this.mfaDisableError);
          this.isDisablingMfa = false;
        });
      }
    });
  };

  clearMfaDisableError = () => {
    runInAction(() => {
      this.mfaDisableError = null;
    });
  };

  logout = () => {
    eventLogout();

    if (this.user) {
      // TD-2220: fire-and-forget release of the BE active_sessions row so the
      // next user logging in on the same email doesn't see "take over previous
      // session" for up to the 30-min lazy-sweep window.
      //
      // logout MUST stay synchronous so the auth-state invariants downstream
      // listeners rely on (useAppSessionGuard, WebSocketStore, pubSub('logout')
      // subscribers in agent adapters that clear localStorage per TD-1845) all
      // see consistent state — that's why we don't await makeRequest() (which
      // would also fail because makeRequest awaits this.getToken() and after
      // signOut() runs synchronously below, getToken returns null).
      //
      // The id token must NOT come from this.user.idToken — that is captured at
      // boot/login and is never updated when the SDK auto-refreshes the token
      // (~1h idToken TTL), so after ~1h tab-open it is expired and the BE drops
      // the call as a 401.
      //
      // It must also NOT be read off cognitoUser.signInUserSession directly:
      // getCognitoUser() constructs a NEW CognitoUser and the SDK constructor
      // sets signInUserSession = null. The property is only populated by
      // authenticateUser / getSession / refreshSession on that same instance,
      // and store.getToken() calls getSession() on a different instance
      // (getUserPool().getCurrentUser()) — it is per-instance state, not
      // shared. Reading it here yielded undefined and skipped the release
      // silently for every logout (TD-2305).
      //
      // getSession() is the supported accessor: it hydrates the instance from
      // storage and, when the cached id token is still valid, invokes its
      // callback SYNCHRONOUSLY — so logout stays synchronous on the normal
      // path and the request is dispatched before signOut() clears storage.
      // fetch() with keepalive:true lets the browser send it even if the page
      // unloads immediately after.
      //
      // If the cached token is already expired, getSession instead goes async
      // via refreshSession — signOut() below has then already run, and the SDK
      // re-caches the refreshed tokens (including LastAuthUser) into storage in
      // its success handler, on top of the storage signOut() just cleared. That
      // would sign the user straight back in on the next page load, so the
      // callback re-clears when it finds itself on that path.
      const cognitoUser = getCognitoUser(this.user.accessToken.payload.username);

      // Set once signOut() below has run, so the callback can tell the async
      // (refreshSession) path from the synchronous cached-token one.
      let signedOut = false;

      try {
        cognitoUser.getSession((error, session) => {
          try {
            const idToken = session?.getIdToken()?.getJwtToken();
            if (error || !idToken) {
              // Always log: TD-2305 went unnoticed for two months precisely
              // because the skip was silent. The 30-min lazy sweep
              // (ACTIVITY_TIMEOUT_MINUTES in td-backend activeSessions.js) is
              // the safety net when the release doesn't fire.
              console.warn('logout: session-end release skipped', error?.name || 'no id token');
            } else {
              // Still fire-and-forget (logout stays synchronous), but hand the
              // in-flight promise to sessionClaim so a same-browser login right
              // after logout waits for the slot to actually free before claiming
              // (TD-2239 bug: the race counted the just-released row → false modal).
              const release = fetch(
                `${import.meta.env.VITE_API_BASEURL}${API_ENDPOINTS.POST_SESSION_END.path}`,
                {
                  method: 'POST',
                  keepalive: true,
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: idToken
                  },
                  body: '{}'
                }
              ).catch(() => {}); // swallow — must not block logout
              recordSessionRelease(release);
            }
          } catch (e) {
            // On the async path this callback runs inside the SDK's response
            // handler, where a throw would be unhandled — keep it contained.
            console.warn('logout: session-end release skipped', e?.name);
          } finally {
            if (signedOut) {
              // Async path: the SDK has just re-cached fresh tokens over the
              // storage signOut() already cleared. signOut() with no argument
              // is cleanClientData() — synchronous, idempotent, no network.
              cognitoUser.signOut();
            }
          }
        });
      } catch (e) {
        // Never block logout on an unexpected error reading the token — log and
        // continue (mirrors the disableMfa catch above). The 30-min lazy sweep
        // is the safety net if the session-end release didn't fire.
        console.warn('logout: session-end release skipped', e?.name);
      }

      cognitoUser.signOut();
      signedOut = true;
    }

    this.root.resetStore();
    pubSub.publish({ event: 'logout' });
  };

  deactivateAccount = () => {
    runInAction(() => (this.isAuthenticating = true));
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.DEACTIVATE_USER_ACCOUNT,
      onSuccess: () => {
        this.userInfo.isDeactivatingAccount = true;
        this.logout();
        this.root.utilsStore.setHeaderMessage('Your account has been deactivated successfully');
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          errorMessage || 'Failed to deactivate account',
          HEADER_MESSAGE_TYPES.ERROR
        );
      },
      onFinally: () => {
        this.isAuthenticating = false;
      }
    });
  };

  changePassword = (oldPassword, newPassword) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      return;
    }

    runInAction(() => {
      this.isAuthenticating = true;
      this.authErrors = {
        messages: [],
        invalidFields: []
      };
    });

    cognitoUser.getSession((err) => {
      if (err) {
        runInAction(() => {
          this.isAuthenticating = false;
          this.authErrors.messages.push(generateMessage(err.message || JSON.stringify(err), false));
        });
      } else {
        cognitoUser.changePassword(oldPassword, newPassword, (err) => {
          runInAction(() => {
            if (err) {
              if (err.code === 'NotAuthorizedException') {
                this.authErrors.messages.push(
                  generateMessage(
                    'Old password is wrong',
                    false,
                    USER_CHANGE_PASSWORD_FIELDS.OLD_PASSWORD.NAME,
                    true
                  )
                );
                this.authErrors.invalidFields.push(USER_CHANGE_PASSWORD_FIELDS.OLD_PASSWORD.NAME);
              } else {
                this.authErrors.messages.push(
                  generateMessage(err.message || JSON.stringify(err), false)
                );
              }
            } else {
              this.authErrors = { messages: [], invalidFields: [] };
              this.root.utilsStore.setHeaderMessage('Password updated sucessfully');
            }
            this.isAuthenticating = false;
          });
        });
      }
    });
  };

  updateUserInfo = (data = {}) => {
    if (this.isUpdatingUserInfo) {
      return;
    }

    runInAction(() => (this.isUpdatingUserInfo = true));
    void this.root.makeRequest({
      endpoint: API_ENDPOINTS.EDIT_USER_INFO,
      body: mapData({ ...this.userInfo, ...data }, USER_PROFILE_FIELDS, true),
      onSuccess: (response) => {
        this.userInfo = mapData(response, USER_PROFILE_FIELDS);
        this.root.utilsStore.setHeaderMessage('User profile updated sucessfully');
      },
      onError: (errorMessage) => {
        this.root.utilsStore.setHeaderMessage(
          errorMessage || 'Failed to update user profile',
          HEADER_MESSAGE_TYPES.ERROR
        );
      },
      onFinally: () => {
        this.isUpdatingUserInfo = false;
      }
    });
  };

  getAccountDetails = () => {
    if (!this.user) {
      runInAction(() => {
        this.userInfo = null;
        this.isAuthenticating = false;
        this.userVerificationStatus = ACCOUNT_VERIF_TYPES.NOT_OBTAINED;
        this.userAccountType = ACCOUNT_TYPES.NOT_OBTAINED;
      });
      return;
    }

    runInAction(() => {
      this.isAuthenticating = true;
      this.userVerificationStatus = ACCOUNT_VERIF_TYPES.OBTAINING;
      this.userAccountType = ACCOUNT_TYPES.OBTAINING;
      this.accountInfoError = null;
    });

    void this.root.makeRequests({
      requests: [
        { endpoint: API_ENDPOINTS.GET_ACCOUNT_VERIFICATION_PROGRESS },
        { endpoint: API_ENDPOINTS.GET_ACCOUNT_TYPE },
        { endpoint: API_ENDPOINTS.GET_PROFILE_COMPANY },
        { endpoint: API_ENDPOINTS.GET_USER_INFO },
        { endpoint: API_ENDPOINTS.GET_WEBSOCKET_TOKEN }
      ],
      onSuccess: (response) => {
        this.userVerificationStatus = response[0];
        this.userAccountType = response[1];
        this.userProfileCompany = mapData(response[2], COMPANY_PROFILE_FIELDS);
        this.userInfo = mapData(response[3], USER_PROFILE_FIELDS);
        this.hasUserProfileCompany = !Object.values(COMPANY_PROFILE_FIELDS)
          .filter((f) => f.REQUIRED)
          .map((r) => r.NAME)
          .find((fieldName) => isEmptyValue(this.userProfileCompany[fieldName]));
        setTimeout(() => this.root.webSocketStore.init(response[4].token), 10);
      },
      onError: (errorMessage) => {
        this.userInfo = null;
        this.userVerificationStatus = ACCOUNT_VERIF_TYPES.NOT_OBTAINED;
        this.userAccountType = ACCOUNT_TYPES.NOT_OBTAINED;
        this.userProfileCompany = null;
        this.hasUserProfileCompany = false;
        this.accountInfoError = errorMessage || 'Failed to fetch account information.';
      },
      onFinally: () => {
        this.isAuthenticating = false;
      }
    });
  };
}

export default AuthStore;
